/**
 * ADR-010 §16.1 / ADR-029 Q9-A — installation identity and durable remote
 * interaction contract.
 *
 * Q9-A is business-behavior-inert. It defines the closed, persistence-safe
 * vocabulary. Q9-B prerequisites advance independently: the external
 * installation-authority source contract may be ready while identity
 * initialization/rekey, one-time remote-mutation permits, and every production
 * writer remain fail-closed.
 */

type Assert<T extends true> = T;
type SameMembers<Left, Right> =
  Exclude<Left, Right> extends never ? (Exclude<Right, Left> extends never ? true : false) : false;

declare const recoveryEpochBrand: unique symbol;
declare const installationGenerationBrand: unique symbol;
declare const installEpochBrand: unique symbol;
declare const installationTransitionIdBrand: unique symbol;
declare const installationTransitionCheckpointStateVersionBrand: unique symbol;
declare const installationAuthorityVersionBrand: unique symbol;
declare const legacyInstallationAdmissionIdBrand: unique symbol;
declare const legacyInstallationAdmissionEvidenceDigestBrand: unique symbol;
declare const freshInstallationGrantIdBrand: unique symbol;
declare const freshInstallationGrantEvidenceDigestBrand: unique symbol;
declare const freshInstallationAdmissionIdBrand: unique symbol;
declare const freshInstallationAdmissionEvidenceDigestBrand: unique symbol;
declare const freshInstallationChallengeNonceBrand: unique symbol;
declare const freshInstallationPermitRequestNonceBrand: unique symbol;
declare const freshInstallationRedemptionPermitDigestBrand: unique symbol;
declare const authorityRecoveryEpochBrand: unique symbol;
declare const authorityInstallationGenerationBrand: unique symbol;
declare const authorityInstallEpochBrand: unique symbol;
declare const remoteMutationPermitReferenceBrand: unique symbol;
declare const remoteMutationIntentIdBrand: unique symbol;
declare const remoteMutationActionIdBrand: unique symbol;
declare const remoteMutationAttemptIdBrand: unique symbol;
declare const remoteMutationOperatorEpisodeBrand: unique symbol;
declare const remoteMutationOperatorQueueIdBrand: unique symbol;
declare const remoteMutationOperatorQueueStateVersionBrand: unique symbol;
declare const remoteMutationOperatorQueueOpenedAtSecondsBrand: unique symbol;
declare const fingerprintKeyVersionBrand: unique symbol;
declare const idempotencyFingerprintBrand: unique symbol;
declare const targetFingerprintBrand: unique symbol;
declare const requestFingerprintBrand: unique symbol;
declare const ownershipFingerprintBrand: unique symbol;
declare const preconditionFingerprintBrand: unique symbol;

/** Q9-A cannot activate a production remote-interaction path. */
export const Q9_INSTALLATION_IDENTITY_RUNTIME_ENABLED = false as const;

/** SQLite INTEGER values consumed by JavaScript must remain exactly representable. */
export const Q9_MAX_SAFE_INTEGER = 9_007_199_254_740_991 as const;
/** Maximum canonical Unix second supported by JavaScript Date (9999-12-31). */
export const Q9_TIMESTAMP_MAX_SECONDS = 253_402_300_799 as const;
export const INSTALLATION_EPOCH_HEX_LENGTH = 32 as const;
export const INSTALLATION_TRANSITION_ID_HEX_LENGTH = 32 as const;
export const LEGACY_INSTALLATION_ADMISSION_ID_HEX_LENGTH = 32 as const;
export const LEGACY_INSTALLATION_ADMISSION_EVIDENCE_DIGEST_HEX_LENGTH = 64 as const;
export const FRESH_INSTALLATION_GRANT_ID_HEX_LENGTH = 32 as const;
export const FRESH_INSTALLATION_ADMISSION_ID_HEX_LENGTH = 32 as const;
export const FRESH_INSTALLATION_EVIDENCE_DIGEST_HEX_LENGTH = 64 as const;
export const FRESH_INSTALLATION_CHALLENGE_NONCE_HEX_LENGTH = 64 as const;
export const FRESH_INSTALLATION_PERMIT_REQUEST_NONCE_HEX_LENGTH = 64 as const;
export const REMOTE_MUTATION_FINGERPRINT_HEX_LENGTH = 64 as const;

/**
 * Closed fingerprint serialization and MAC primitive. Q9-B1 closes every
 * domain's ordered business-field tuple below. A later writer still cannot
 * discharge the F-03 gate until the independent ownership-marker round-trip,
 * keyring, authority, fence, provider, adjudication, and persistence-runtime
 * prerequisites are proven READY.
 *
 * The domain tag is always the first frame. Each following field is encoded in
 * its declared order as three frames:
 *   LP(field-name) || LP(type-tag) || LP(canonical-value)
 * where LP(value) is:
 *   ASCII decimal UTF-8-byte-length ":" raw UTF-8 bytes
 * and the complete preimage is:
 *   LP(domain-tag) || field-frame-1 || ... || field-frame-N
 * Unsigned safe integers use canonical base-10 with no sign or leading zero;
 * enum values use their exact ASCII spelling; strings are Unicode NFC; NULL
 * uses type-tag `NULL` with a zero-byte canonical value and is never conflated
 * with empty text (`NFC_UTF8` with a zero-byte value).
 * HMAC-SHA-256 produces 64 lowercase hexadecimal characters.
 *
 * Persisted key_version selects a historical verification key. MAC preimages,
 * key material, and raw remote payloads are never stored; typed target
 * coordinates and closed metadata may be stored as ordinary columns.
 */
export const REMOTE_MUTATION_FINGERPRINT_CONTRACT_VERSION = 'Q9-LP-UTF8-HMAC-SHA256-V1' as const;
export type RemoteMutationFingerprintContractVersion =
  typeof REMOTE_MUTATION_FINGERPRINT_CONTRACT_VERSION;

export const REMOTE_MUTATION_FINGERPRINT_DOMAIN_TAGS = Object.freeze({
  IDEMPOTENCY: 'find-my-part:q9:idempotency:v1',
  TARGET: 'find-my-part:q9:target:v1',
  REQUEST: 'find-my-part:q9:request:v1',
  OWNERSHIP: 'find-my-part:q9:ownership:v1',
  PRECONDITION: 'find-my-part:q9:precondition:v1',
} as const);

/** Machine-readable canonicalization rules shared by writers and verifier tests. */
export const REMOTE_MUTATION_FINGERPRINT_CANONICALIZATION = Object.freeze({
  domainPosition: 'FIRST_FRAME',
  fieldForm: 'DECLARED_ORDER_NAMED_TYPED_VALUE',
  framing: 'ASCII_UTF8_BYTE_LENGTH_COLON_RAW_UTF8',
  frameComposition: 'LP_DOMAIN_THEN_LP_NAME_LP_TYPE_LP_VALUE_PER_FIELD',
  unsignedSafeInteger: 'BASE10_NO_SIGN_NO_LEADING_ZERO',
  enumValue: 'EXACT_ASCII_SPELLING',
  stringNormalization: 'UNICODE_NFC',
  nullEncoding: 'EXPLICIT_TYPED_NULL_TAG',
  nullCanonicalValue: 'ZERO_BYTE_VALUE',
  mac: 'HMAC_SHA256_LOWERCASE_HEX',
  forbiddenPersistence: Object.freeze([
    'MAC_PREIMAGE',
    'KEY_MATERIAL',
    'RAW_REMOTE_PAYLOAD',
  ] as const),
} as const);

export const REMOTE_MUTATION_FINGERPRINT_TYPE_TAGS = Object.freeze({
  ENUM_ASCII: 'ENUM_ASCII',
  ASCII_IDENTIFIER: 'ASCII_ID',
  UNSIGNED_SAFE_INTEGER: 'U53',
  NFC_STRING: 'NFC_UTF8',
  NULL: 'NULL',
} as const);

export const REMOTE_MUTATION_FINGERPRINT_BOOLEAN_ENUM = Object.freeze({
  true: 'TRUE',
  false: 'FALSE',
} as const);

/**
 * Hostile-input ceilings for the canonical fingerprint representation.
 *
 * The raw-code-unit ceiling is checked before Unicode scanning/normalization;
 * its deliberate 2× relationship to the normalized UTF-8 byte ceiling bounds
 * pathological decomposed input without pretending raw and normalized lengths
 * are equivalent. The normalized ceiling remains the representation limit.
 * Revisit both together if the canonicalization contract version changes.
 */
export const REMOTE_MUTATION_FINGERPRINT_TEXT_LIMITS = Object.freeze({
  maxRawCodeUnitsPerTextField: 2_097_152,
  maxNormalizedUtf8BytesPerTextField: 1_048_576,
  maxCanonicalPreimageBytes: 2_097_152,
} as const);

export type RemoteMutationFingerprintFrameType =
  (typeof REMOTE_MUTATION_FINGERPRINT_TYPE_TAGS)[keyof typeof REMOTE_MUTATION_FINGERPRINT_TYPE_TAGS];

export interface RemoteMutationFingerprintFieldSchema {
  readonly fieldName: string;
  readonly allowedTypeTags: readonly RemoteMutationFingerprintFrameType[];
}

function freezeFingerprintFieldSchema<
  const Schema extends readonly RemoteMutationFingerprintFieldSchema[],
>(schema: Schema): Schema {
  for (const field of schema) {
    Object.freeze(field.allowedTypeTags);
    Object.freeze(field);
  }
  return Object.freeze(schema);
}

export const REMOTE_MUTATION_TARGET_FINGERPRINT_SCHEMA = freezeFingerprintFieldSchema([
  { fieldName: 'provider', allowedTypeTags: ['ENUM_ASCII'] },
  { fieldName: 'resource', allowedTypeTags: ['ENUM_ASCII'] },
  { fieldName: 'subject', allowedTypeTags: ['ENUM_ASCII'] },
  { fieldName: 'subject_id', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'channel_id', allowedTypeTags: ['U53'] },
] as const satisfies readonly RemoteMutationFingerprintFieldSchema[]);

export const REMOTE_MUTATION_FINGERPRINT_FIELD_TUPLES = Object.freeze({
  TARGET: Object.freeze(['provider', 'resource', 'subject', 'subject_id', 'channel_id'] as const),
  IDEMPOTENCY: Object.freeze([
    'store_hash',
    'recovery_epoch',
    'installation_generation',
    'install_epoch',
    'source_kind',
    'source_id',
  ] as const),
  REQUEST: Object.freeze([
    'target_fingerprint',
    'operation',
    'expected_remote_id',
    'name',
    'page_type',
    'url',
    'body',
    'is_visible',
    'meta_title',
    'meta_description',
    'search_keywords',
    'channel_id',
    'parent_id',
  ] as const),
  OWNERSHIP: Object.freeze([
    'ownership_marker_version',
    'store_hash',
    'recovery_epoch',
    'installation_generation',
    'install_epoch',
    'target_fingerprint',
    'owner_intent_id',
    'owner_action_id',
  ] as const),
  PRECONDITION: Object.freeze([
    'target_fingerprint',
    'operation',
    'remote_presence',
    'remote_id',
    'ownership_fingerprint',
    'name',
    'page_type',
    'url',
    'body',
    'is_visible',
    'meta_title',
    'meta_description',
    'search_keywords',
    'channel_id',
    'parent_id',
  ] as const),
} as const);

/**
 * Closing the OWNERSHIP tuple does not prove that the marker survives a real
 * BigCommerce create/update/read cycle or that a compensation observes the
 * exact forward action's ownership. Q9-B2/B3 must prove that binding before
 * any writer is enabled.
 */
export const REMOTE_MUTATION_COMPENSATION_OWNERSHIP_BINDING =
  'Q9_B_REQUIRED_BEFORE_WRITER' as const;

/**
 * Rotation is fail-closed: Q9-B must derive/probe the idempotency candidate
 * under every retained verification key version in one transaction or
 * strongly-consistent CAS before insertion. A key cannot retire while any
 * retained intent/action/attempt references its version.
 */
export const REMOTE_MUTATION_FINGERPRINT_ROTATION_RULES = {
  insertDedupe: 'PROBE_ALL_RETAINED_KEY_VERSIONS_ATOMICALLY',
  keyRetirement: 'BLOCK_WHILE_DURABLE_ROWS_REFERENCE_KEY_VERSION',
} as const;

/**
 * Readiness ledger for Q9 first-writer prerequisites. Source readiness and
 * operational readiness are deliberately separate: a READY source entry means
 * the named, independently revertible source contract and its local runtime
 * evidence exist, while the corresponding operational entry remains blocked
 * until the production resource is provisioned, deployed, and read back.
 *
 * The F-03 reverse guard still refuses an installation-identity discharge
 * unless its narrower runtime/lifecycle gates are closed, and refuses every
 * remote-table discharge until all entries are READY.
 */
export const REMOTE_MUTATION_FIRST_WRITER_PREREQUISITES = {
  externalRecoveryAuthoritySource: 'READY',
  externalRecoveryAuthority: 'BLOCKED_Q9_B',
  installationLifecycleOrchestration: 'BLOCKED_Q9_B',
  oneTimePermitBinding: 'BLOCKED_Q9_B',
  fingerprintKeyring: 'BLOCKED_Q9_B',
  compensationOwnershipBinding: 'BLOCKED_Q9_B',
  prePostFenceRuntime: 'BLOCKED_Q9_B',
  authorizedAdjudication: 'BLOCKED_Q9_B',
  providerOutcomeSemantics: 'BLOCKED_Q9_B',
  localDomainCas: 'BLOCKED_Q9_B',
} as const;
export type RemoteMutationFirstWriterPrerequisiteStatus = 'BLOCKED_Q9_B' | 'READY';

/** Origin 128-bit epoch recorded when the logical intent was created. */
export type RecoveryEpoch = string & {
  readonly [recoveryEpochBrand]: 'RecoveryEpoch';
};

/** Positive, monotonically increasing generation within one tenant history. */
export type InstallationGeneration = number & {
  readonly [installationGenerationBrand]: 'InstallationGeneration';
};

/** Zero is reserved for a store whose identity has never been initialized. */
export type InstallationGenerationHighWater = 0 | InstallationGeneration;

/** Origin installation epoch recorded when the logical intent was created. */
export type InstallEpoch = string & {
  readonly [installEpochBrand]: 'InstallEpoch';
};

/**
 * Opaque 128-bit identifier for one exact installation-authority transition.
 * The retained D1 checkpoint stores only its canonical lowercase-hex form;
 * it never embeds a tenant, token, epoch, or other business identifier.
 */
export type InstallationTransitionId = string & {
  readonly [installationTransitionIdBrand]: 'InstallationTransitionId';
};

/**
 * Monotonic per-store CAS high-water for the retained transition checkpoint.
 * It never resets when a finalized transition is replaced by later work.
 */
export type InstallationTransitionCheckpointStateVersion = number & {
  readonly [installationTransitionCheckpointStateVersionBrand]: 'InstallationTransitionCheckpointStateVersion';
};

/** Monotonic version observed from the external installation authority. */
export type InstallationAuthorityVersion = number & {
  readonly [installationAuthorityVersionBrand]: 'InstallationAuthorityVersion';
};

/**
 * Opaque one-time identifier issued by the separately controlled legacy
 * installation-admission campaign. It contains no tenant or provider data.
 */
export type LegacyInstallationAdmissionId = string & {
  readonly [legacyInstallationAdmissionIdBrand]: 'LegacyInstallationAdmissionId';
};

/** SHA-256 of the exact signed legacy-admission bearer envelope. */
export type LegacyInstallationAdmissionEvidenceDigest = string & {
  readonly [legacyInstallationAdmissionEvidenceDigestBrand]: 'LegacyInstallationAdmissionEvidenceDigest';
};

/** Opaque identifier of one externally recorded, provider-authorized grant. */
export type FreshInstallationGrantId = string & {
  readonly [freshInstallationGrantIdBrand]: 'FreshInstallationGrantId';
};

/** SHA-256 of the exact signed fresh-install grant envelope. */
export type FreshInstallationGrantEvidenceDigest = string & {
  readonly [freshInstallationGrantEvidenceDigestBrand]: 'FreshInstallationGrantEvidenceDigest';
};

/** Opaque one-time identifier issued by the independent tenant-root witness. */
export type FreshInstallationAdmissionId = string & {
  readonly [freshInstallationAdmissionIdBrand]: 'FreshInstallationAdmissionId';
};

/** SHA-256 of the exact signed fresh-install admission envelope. */
export type FreshInstallationAdmissionEvidenceDigest = string & {
  readonly [freshInstallationAdmissionEvidenceDigestBrand]: 'FreshInstallationAdmissionEvidenceDigest';
};

/** Authority-generated 256-bit nonce binding one non-authorizing challenge. */
export type FreshInstallationChallengeNonce = string & {
  readonly [freshInstallationChallengeNonceBrand]: 'FreshInstallationChallengeNonce';
};

/** Authority-generated 256-bit nonce for one live install-permit request. */
export type FreshInstallationPermitRequestNonce = string & {
  readonly [freshInstallationPermitRequestNonceBrand]: 'FreshInstallationPermitRequestNonce';
};

/** SHA-256 of the exact live witness permit used to reserve generation one. */
export type FreshInstallationRedemptionPermitDigest = string & {
  readonly [freshInstallationRedemptionPermitDigestBrand]: 'FreshInstallationRedemptionPermitDigest';
};

/** Closed stable lifecycle states eligible for the one-time legacy cutover. */
export const LEGACY_INSTALLATION_SOURCE_LIFECYCLE_STATES = [
  'ACTIVE',
  'UNINSTALLED',
  'PENDING_DELETION',
  'DELETED',
] as const;
export type LegacyInstallationSourceLifecycleState =
  (typeof LEGACY_INSTALLATION_SOURCE_LIFECYCLE_STATES)[number];

/** Final aggregate authority phases retained by an exact transition. */
export const INSTALLATION_AUTHORITY_TARGET_PHASES = ['CURRENT', 'TOMBSTONED', 'DESTROYED'] as const;
export type InstallationAuthorityTargetPhase =
  (typeof INSTALLATION_AUTHORITY_TARGET_PHASES)[number];

/**
 * Authority-local consumption state. `CONSUMED` does not claim that the
 * independent admission register has reached its later terminal `REDEEMED`
 * event; that event requires authority finalization plus exact D1 readback.
 */
export const LEGACY_INSTALLATION_ADMISSION_STATUSES = ['PREAUTHORIZED', 'CONSUMED'] as const;
export type LegacyInstallationAdmissionStatus =
  (typeof LEGACY_INSTALLATION_ADMISSION_STATUSES)[number];

export const INSTALLATION_TRANSITION_KINDS = [
  'INSTALL',
  'REINSTALL',
  'UNINSTALL',
  'DESTRUCTION',
  'RESTORE_REKEY',
  'REPROVISION_AFTER_DESTRUCTION',
  'BOOTSTRAP_LEGACY',
] as const;
export type InstallationTransitionKind = (typeof INSTALLATION_TRANSITION_KINDS)[number];

/**
 * Retained local progress only. No stage grants installation or remote-write
 * authority; callers must reconcile the external authority and guarded local
 * postcondition before advancing it.
 */
export const INSTALLATION_TRANSITION_CHECKPOINT_STAGES = [
  'PREPARED',
  'RESERVED',
  'LOCAL_APPLIED',
  'FINALIZED',
] as const;
export type InstallationTransitionCheckpointStage =
  (typeof INSTALLATION_TRANSITION_CHECKPOINT_STAGES)[number];

/**
 * Exact non-empty checkpoint projected from the retained `stores` anchor.
 * An empty store uses fail-closed database defaults and is represented by the
 * repository as `null`, never as a fabricated transition. Target phase is
 * retained for every kind. The three legacy-admission fields are all present
 * only for `BOOTSTRAP_LEGACY` and all null for every ordinary transition.
 */
export interface InstallationTransitionCheckpoint {
  readonly storeHash: string;
  readonly transitionId: InstallationTransitionId;
  readonly kind: InstallationTransitionKind;
  readonly stage: InstallationTransitionCheckpointStage;
  readonly stateVersion: InstallationTransitionCheckpointStateVersion;
  readonly expectedAuthorityVersion: InstallationAuthorityVersion;
  readonly restoredGeneration: InstallationGenerationHighWater | null;
  readonly targetPhase: InstallationAuthorityTargetPhase;
  readonly legacyAdmissionId: LegacyInstallationAdmissionId | null;
  readonly legacyAdmissionEvidenceDigest: LegacyInstallationAdmissionEvidenceDigest | null;
  readonly legacySourceLifecycleState: LegacyInstallationSourceLifecycleState | null;
}

/**
 * Distinct brands for the authority/current identity used to authorize one
 * interaction. They intentionally cannot be substituted for the durable
 * origin identity without an explicit equality check.
 */
export type AuthorityRecoveryEpoch = string & {
  readonly [authorityRecoveryEpochBrand]: 'AuthorityRecoveryEpoch';
};
export type AuthorityInstallationGeneration = number & {
  readonly [authorityInstallationGenerationBrand]: 'AuthorityInstallationGeneration';
};
export type AuthorityInstallEpoch = string & {
  readonly [authorityInstallEpochBrand]: 'AuthorityInstallEpoch';
};

/**
 * Required non-secret 32-hex, DB-unique reference slot; never the bearer
 * permit. CSPRNG generation, freshness/nonreuse, binding, and external
 * one-time redemption remain Q9-B responsibilities.
 */
export type RemoteMutationPermitReference = string & {
  readonly [remoteMutationPermitReferenceBrand]: 'RemoteMutationPermitReference';
};

export type RemoteMutationIntentId = string & {
  readonly [remoteMutationIntentIdBrand]: 'RemoteMutationIntentId';
};

export type RemoteMutationActionId = string & {
  readonly [remoteMutationActionIdBrand]: 'RemoteMutationActionId';
};

export type RemoteMutationAttemptId = string & {
  readonly [remoteMutationAttemptIdBrand]: 'RemoteMutationAttemptId';
};

/** Positive, monotonically increasing operator episode within one intent. */
export type RemoteMutationOperatorEpisode = number & {
  readonly [remoteMutationOperatorEpisodeBrand]: 'RemoteMutationOperatorEpisode';
};

/** Fresh durable queue identity; a referenced Q9 incident is never reopened. */
export type RemoteMutationOperatorQueueId = string & {
  readonly [remoteMutationOperatorQueueIdBrand]: 'RemoteMutationOperatorQueueId';
};

/** Queue lifecycle version captured when an intent enters its operator episode. */
export type RemoteMutationOperatorQueueStateVersion = number & {
  readonly [remoteMutationOperatorQueueStateVersionBrand]: 'RemoteMutationOperatorQueueStateVersion';
};

/** Canonical Unix-second `opened_at` captured for the same queue episode. */
export type RemoteMutationOperatorQueueOpenedAtSeconds = number & {
  readonly [remoteMutationOperatorQueueOpenedAtSecondsBrand]: 'RemoteMutationOperatorQueueOpenedAtSeconds';
};

/**
 * Complete durable identity of the current/last operator episode. Adjudication
 * copies this tuple exactly. `queueStateVersion` is the entry vintage, not the
 * mutable live acknowledgement/snooze counter; the referenced queue may only
 * advance from it and can never reopen.
 */
export interface RemoteMutationOperatorEpisodeBinding {
  readonly operatorEpisode: RemoteMutationOperatorEpisode;
  readonly queueId: RemoteMutationOperatorQueueId;
  readonly queueStateVersion: RemoteMutationOperatorQueueStateVersion;
  readonly queueOpenedAt: RemoteMutationOperatorQueueOpenedAtSeconds;
  readonly actionId: RemoteMutationActionId;
  readonly executeAttemptId: RemoteMutationAttemptId;
}

/** Exact queue identity used for a Q9 operator-required incident. */
export interface RemoteMutationOperatorIncidentKey {
  readonly keyKind: 'CONDITION';
  readonly keyPart1: 'Q9_REMOTE_MUTATION_INTENT';
  readonly keyPart2: RemoteMutationIntentId;
}

export const REMOTE_MUTATION_OPERATOR_INCIDENT_EVIDENCE_CODE =
  'Q9_REMOTE_MUTATION_ESCALATION' as const;
export type RemoteMutationOperatorIncidentEvidenceCode =
  typeof REMOTE_MUTATION_OPERATOR_INCIDENT_EVIDENCE_CODE;

export const REMOTE_MUTATION_OPERATOR_INCIDENT_FIXED_FIELDS = {
  keyKind: 'CONDITION',
  keyPart1: 'Q9_REMOTE_MUTATION_INTENT',
  openingClass: 'OPERATOR_CONDITION',
  reasonCode: 'OPERATOR_CONDITION',
  disposition: 'OPERATOR_ACTION_REQUIRED',
  evidenceCode: REMOTE_MUTATION_OPERATOR_INCIDENT_EVIDENCE_CODE,
} as const;

/** Positive version selecting a retained historical HMAC verification key. */
export type RemoteMutationFingerprintKeyVersion = number & {
  readonly [fingerprintKeyVersionBrand]: 'RemoteMutationFingerprintKeyVersion';
};

export type RemoteMutationIdempotencyFingerprint = string & {
  readonly [idempotencyFingerprintBrand]: 'RemoteMutationIdempotencyFingerprint';
};
export type RemoteMutationTargetFingerprint = string & {
  readonly [targetFingerprintBrand]: 'RemoteMutationTargetFingerprint';
};
export type RemoteMutationRequestFingerprint = string & {
  readonly [requestFingerprintBrand]: 'RemoteMutationRequestFingerprint';
};
export type RemoteMutationOwnershipFingerprint = string & {
  readonly [ownershipFingerprintBrand]: 'RemoteMutationOwnershipFingerprint';
};
export type RemoteMutationPreconditionFingerprint = string & {
  readonly [preconditionFingerprintBrand]: 'RemoteMutationPreconditionFingerprint';
};

/** The current installation identity is exactly this three-part tuple. */
export interface InstallationIdentityTriple {
  readonly recoveryEpoch: RecoveryEpoch;
  readonly installationGeneration: InstallationGeneration;
  readonly installEpoch: InstallEpoch;
}
export type OriginInstallationIdentityTriple = InstallationIdentityTriple;

export interface AuthorityInstallationIdentityTriple {
  readonly recoveryEpoch: AuthorityRecoveryEpoch;
  readonly installationGeneration: AuthorityInstallationGeneration;
  readonly installEpoch: AuthorityInstallEpoch;
}

export const STORE_INSTALLATION_IDENTITY_STATES = [
  'UNINITIALIZED',
  'CURRENT',
  'INACTIVE',
  'REKEY_REQUIRED',
  'TOMBSTONED',
] as const;
export type StoreInstallationIdentityState = (typeof STORE_INSTALLATION_IDENTITY_STATES)[number];

/**
 * Q9-B1 owner decision: TOMBSTONED is terminal for one installation
 * incarnation. Re-entry is an externally authorized rekey to exactly N + 1;
 * generation reuse, skips, regression, wraparound, and laundering through
 * INACTIVE are forbidden. REKEY_REQUIRED is fail-closed and grants no remote
 * mutation authority.
 *
 * INACTIVE's independent pause/resume semantics are not authorized here. This
 * policy only prevents TOMBSTONED from using INACTIVE as a same-generation
 * escape route.
 */
export const INSTALLATION_IDENTITY_TOMBSTONE_REBIND_POLICY = {
  tombstonedIncarnationIsTerminal: true,
  allowedExitStates: ['REKEY_REQUIRED', 'CURRENT'],
  requiredGenerationDelta: 1,
  requireFreshInstallEpoch: true,
  restoreRequiresExternalRecoveryEpochRotation: true,
  rekeyRequiredAuthorizesRemoteMutation: false,
  forbidIndirectExitThroughInactive: true,
  generationExhaustion: 'FAIL_CLOSED',
} as const satisfies {
  readonly tombstonedIncarnationIsTerminal: true;
  readonly allowedExitStates: readonly Extract<
    StoreInstallationIdentityState,
    'REKEY_REQUIRED' | 'CURRENT'
  >[];
  readonly requiredGenerationDelta: 1;
  readonly requireFreshInstallEpoch: true;
  readonly restoreRequiresExternalRecoveryEpochRotation: true;
  readonly rekeyRequiredAuthorizesRemoteMutation: false;
  readonly forbidIndirectExitThroughInactive: true;
  readonly generationExhaustion: 'FAIL_CLOSED';
};

export type InstallationIdentityTombstoneRebindDecision =
  | {
      readonly kind: 'NOT_APPLICABLE';
      readonly policyEligible: false;
      readonly writerAuthorized: false;
    }
  | {
      readonly kind: 'TOMBSTONE_UNCHANGED';
      readonly policyEligible: true;
      readonly writerAuthorized: false;
    }
  | {
      readonly kind: 'NEXT_GENERATION_REQUIRES_EXTERNAL_AUTHORITY';
      readonly policyEligible: true;
      readonly writerAuthorized: false;
      readonly externalAuthorityRequired: true;
    }
  | {
      readonly kind:
        | 'ILLEGAL_EXIT_STATE'
        | 'GENERATION_EXHAUSTED'
        | 'GENERATION_MISMATCH'
        | 'INSTALL_EPOCH_REUSE'
        | 'INVALID_GENERATION'
        | 'INVALID_INPUT';
      readonly policyEligible: false;
      readonly writerAuthorized: false;
    };

const TOMBSTONE_REBIND_INPUT_KEYS = [
  'currentState',
  'currentGeneration',
  'requestedState',
  'requestedGeneration',
  'requestedInstallEpochWasPreviouslyUsed',
] as const;

interface InstallationIdentityTombstoneRebindInput {
  readonly currentState: StoreInstallationIdentityState;
  readonly currentGeneration: number;
  readonly requestedState: StoreInstallationIdentityState;
  readonly requestedGeneration: number;
  readonly requestedInstallEpochWasPreviouslyUsed: boolean;
}

function isStoreInstallationIdentityState(value: unknown): value is StoreInstallationIdentityState {
  return (
    value === 'UNINITIALIZED' ||
    value === 'CURRENT' ||
    value === 'INACTIVE' ||
    value === 'REKEY_REQUIRED' ||
    value === 'TOMBSTONED'
  );
}

function snapshotTombstoneRebindInput(
  input: unknown
): InstallationIdentityTombstoneRebindInput | null {
  try {
    if (typeof input !== 'object' || input === null || Array.isArray(input)) {
      return null;
    }
    const prototype = Object.getPrototypeOf(input);
    if (prototype !== Object.prototype && prototype !== null) return null;
    if (Object.getOwnPropertySymbols(input).length !== 0) return null;

    const descriptors = Object.getOwnPropertyDescriptors(input);
    const names = Object.keys(descriptors);
    if (
      names.length !== TOMBSTONE_REBIND_INPUT_KEYS.length ||
      !TOMBSTONE_REBIND_INPUT_KEYS.every((key) =>
        Object.prototype.hasOwnProperty.call(descriptors, key)
      )
    ) {
      return null;
    }

    const values = Object.create(null) as Record<string, unknown>;
    for (const key of TOMBSTONE_REBIND_INPUT_KEYS) {
      const descriptor = descriptors[key];
      if (
        descriptor === undefined ||
        !descriptor.enumerable ||
        !Object.prototype.hasOwnProperty.call(descriptor, 'value')
      ) {
        return null;
      }
      values[key] = descriptor.value;
    }

    if (
      !isStoreInstallationIdentityState(values.currentState) ||
      !isStoreInstallationIdentityState(values.requestedState) ||
      typeof values.currentGeneration !== 'number' ||
      typeof values.requestedGeneration !== 'number' ||
      typeof values.requestedInstallEpochWasPreviouslyUsed !== 'boolean'
    ) {
      return null;
    }

    return Object.freeze({
      currentState: values.currentState,
      currentGeneration: values.currentGeneration,
      requestedState: values.requestedState,
      requestedGeneration: values.requestedGeneration,
      requestedInstallEpochWasPreviouslyUsed: values.requestedInstallEpochWasPreviouslyUsed,
    });
  } catch {
    return null;
  }
}

/**
 * Pure policy arbiter used before any persistence adapter. An eligible result
 * is not external authorization and does not enable a writer; the Q9-B2
 * authority reserve/CAS remains mandatory.
 */
export function arbitrateInstallationIdentityTombstoneRebind(
  input: unknown
): InstallationIdentityTombstoneRebindDecision {
  const snapshot = snapshotTombstoneRebindInput(input);
  if (snapshot === null) {
    return {
      kind: 'INVALID_INPUT',
      policyEligible: false,
      writerAuthorized: false,
    };
  }

  if (
    !Number.isSafeInteger(snapshot.currentGeneration) ||
    !Number.isSafeInteger(snapshot.requestedGeneration) ||
    snapshot.currentGeneration < 0 ||
    snapshot.requestedGeneration < 0 ||
    snapshot.currentGeneration > Q9_MAX_SAFE_INTEGER ||
    snapshot.requestedGeneration > Q9_MAX_SAFE_INTEGER
  ) {
    return {
      kind: 'INVALID_GENERATION',
      policyEligible: false,
      writerAuthorized: false,
    };
  }

  if (snapshot.currentState !== 'TOMBSTONED') {
    return {
      kind: 'NOT_APPLICABLE',
      policyEligible: false,
      writerAuthorized: false,
    };
  }

  if (snapshot.currentGeneration < 1 || snapshot.requestedGeneration < 1) {
    return {
      kind: 'INVALID_GENERATION',
      policyEligible: false,
      writerAuthorized: false,
    };
  }

  if (snapshot.requestedState === 'TOMBSTONED') {
    return snapshot.requestedGeneration === snapshot.currentGeneration
      ? {
          kind: 'TOMBSTONE_UNCHANGED',
          policyEligible: true,
          writerAuthorized: false,
        }
      : {
          kind: 'GENERATION_MISMATCH',
          policyEligible: false,
          writerAuthorized: false,
        };
  }

  if (snapshot.requestedState !== 'REKEY_REQUIRED' && snapshot.requestedState !== 'CURRENT') {
    return {
      kind: 'ILLEGAL_EXIT_STATE',
      policyEligible: false,
      writerAuthorized: false,
    };
  }

  if (snapshot.currentGeneration === Q9_MAX_SAFE_INTEGER) {
    return {
      kind: 'GENERATION_EXHAUSTED',
      policyEligible: false,
      writerAuthorized: false,
    };
  }

  if (snapshot.requestedGeneration !== snapshot.currentGeneration + 1) {
    return {
      kind: 'GENERATION_MISMATCH',
      policyEligible: false,
      writerAuthorized: false,
    };
  }

  if (snapshot.requestedInstallEpochWasPreviouslyUsed) {
    return {
      kind: 'INSTALL_EPOCH_REUSE',
      policyEligible: false,
      writerAuthorized: false,
    };
  }

  return {
    kind: 'NEXT_GENERATION_REQUIRES_EXTERNAL_AUTHORITY',
    policyEligible: true,
    writerAuthorized: false,
    externalAuthorityRequired: true,
  };
}

export const TENANT_INSTALLATION_IDENTITY_STATES = ['CURRENT', 'SUPERSEDED'] as const;
export type TenantInstallationIdentityState = (typeof TENANT_INSTALLATION_IDENTITY_STATES)[number];

export const TENANT_INSTALLATION_IDENTITY_END_REASONS = [
  'UNINSTALL',
  'REINSTALL',
  'RECOVERY_REKEY',
  'OPERATOR_REKEY',
] as const;
export type TenantInstallationIdentityEndReason =
  (typeof TENANT_INSTALLATION_IDENTITY_END_REASONS)[number];

export const REMOTE_MUTATION_PROVIDERS = ['BIGCOMMERCE'] as const;
export type RemoteMutationProvider = (typeof REMOTE_MUTATION_PROVIDERS)[number];

export const REMOTE_MUTATION_RESOURCES = ['PAGE'] as const;
export type RemoteMutationResource = (typeof REMOTE_MUTATION_RESOURCES)[number];

export const REMOTE_MUTATION_OPERATIONS = ['CREATE', 'UPDATE', 'DELETE', 'HIDE'] as const;
export type RemoteMutationOperation = (typeof REMOTE_MUTATION_OPERATIONS)[number];

export const REMOTE_MUTATION_SUBJECTS = [
  'QUESTION_DETAIL',
  'DISCOVERY_INDEX',
  'HUB',
  'NAV_SHROUD',
  'DIAGNOSTIC_PREFLIGHT',
  'DIAGNOSTIC_PROBE',
] as const;
export type RemoteMutationSubject = (typeof REMOTE_MUTATION_SUBJECTS)[number];

export const REMOTE_MUTATION_COMPENSATION_POLICIES = [
  'DELETE_OWNED_CREATE',
  'HIDE_OWNED_CREATE',
  'RECONCILE_OWNED_PAGE',
  'OPERATOR_REQUIRED',
] as const;
export type RemoteMutationCompensationPolicy =
  (typeof REMOTE_MUTATION_COMPENSATION_POLICIES)[number];

/**
 * Stable logical intent lifecycle. Unknown and operator states are deliberately
 * active/target-blocking. OPERATOR_REQUIRED is resolvable and not terminal.
 */
export const REMOTE_MUTATION_INTENT_STATES = [
  'PREPARED',
  'FORWARD_PENDING',
  'FORWARD_OUTCOME_UNKNOWN',
  'DESIRED_REMOTE_STATE_CONFIRMED',
  'LOCAL_COMMITTED',
  'COMPENSATION_PENDING',
  'COMPENSATING',
  'COMPENSATION_OUTCOME_UNKNOWN',
  'COMPENSATED',
  'OPERATOR_REQUIRED',
  'CANCELLED',
] as const;
export type RemoteMutationIntentState = (typeof REMOTE_MUTATION_INTENT_STATES)[number];

export const REMOTE_MUTATION_INTENT_TRANSITIONS = {
  PREPARED: ['FORWARD_PENDING', 'CANCELLED'],
  FORWARD_PENDING: [
    'FORWARD_OUTCOME_UNKNOWN',
    'DESIRED_REMOTE_STATE_CONFIRMED',
    'COMPENSATION_PENDING',
    'OPERATOR_REQUIRED',
    'CANCELLED',
  ],
  FORWARD_OUTCOME_UNKNOWN: [
    'FORWARD_PENDING',
    'DESIRED_REMOTE_STATE_CONFIRMED',
    'COMPENSATION_PENDING',
    'OPERATOR_REQUIRED',
  ],
  DESIRED_REMOTE_STATE_CONFIRMED: ['LOCAL_COMMITTED', 'COMPENSATION_PENDING'],
  LOCAL_COMMITTED: [],
  COMPENSATION_PENDING: ['COMPENSATING'],
  COMPENSATING: ['COMPENSATION_OUTCOME_UNKNOWN', 'COMPENSATED', 'OPERATOR_REQUIRED'],
  COMPENSATION_OUTCOME_UNKNOWN: ['COMPENSATING', 'COMPENSATED', 'OPERATOR_REQUIRED'],
  COMPENSATED: [],
  OPERATOR_REQUIRED: [
    'FORWARD_PENDING',
    'DESIRED_REMOTE_STATE_CONFIRMED',
    'LOCAL_COMMITTED',
    'COMPENSATION_PENDING',
    'COMPENSATING',
    'COMPENSATED',
    'CANCELLED',
  ],
  CANCELLED: [],
} as const satisfies Readonly<
  Record<RemoteMutationIntentState, readonly RemoteMutationIntentState[]>
>;

export const REMOTE_MUTATION_INTENT_TERMINAL_STATES = [
  'LOCAL_COMMITTED',
  'COMPENSATED',
  'CANCELLED',
] as const satisfies readonly RemoteMutationIntentState[];
export type RemoteMutationIntentTerminalState =
  (typeof REMOTE_MUTATION_INTENT_TERMINAL_STATES)[number];

export const REMOTE_MUTATION_TARGET_BLOCKING_INTENT_STATES = [
  'PREPARED',
  'FORWARD_PENDING',
  'FORWARD_OUTCOME_UNKNOWN',
  'DESIRED_REMOTE_STATE_CONFIRMED',
  'COMPENSATION_PENDING',
  'COMPENSATING',
  'COMPENSATION_OUTCOME_UNKNOWN',
  'OPERATOR_REQUIRED',
] as const satisfies readonly RemoteMutationIntentState[];

export const REMOTE_MUTATION_ACTION_KINDS = ['FORWARD', 'COMPENSATION'] as const;
export type RemoteMutationActionKind = (typeof REMOTE_MUTATION_ACTION_KINDS)[number];

/**
 * One immutable concrete mutation description. A retry reuses this action;
 * compensation or a materially changed mutation creates another PREPARED
 * action so its operation/request/precondition/ownership evidence is durable.
 */
export const REMOTE_MUTATION_ACTION_STATES = [
  'PREPARED',
  'ATTEMPT_ACTIVE',
  'RETRYABLE_KNOWN_NO_EFFECT',
  'TERMINAL_KNOWN_NO_EFFECT',
  'OUTCOME_UNKNOWN',
  'DESIRED_REMOTE_STATE_CONFIRMED',
  'OPERATOR_REQUIRED',
  'SUPERSEDED',
] as const;
export type RemoteMutationActionState = (typeof REMOTE_MUTATION_ACTION_STATES)[number];

export const REMOTE_MUTATION_ACTION_TRANSITIONS = {
  PREPARED: ['ATTEMPT_ACTIVE', 'OPERATOR_REQUIRED', 'SUPERSEDED'],
  ATTEMPT_ACTIVE: [
    'RETRYABLE_KNOWN_NO_EFFECT',
    'TERMINAL_KNOWN_NO_EFFECT',
    'OUTCOME_UNKNOWN',
    'DESIRED_REMOTE_STATE_CONFIRMED',
    'OPERATOR_REQUIRED',
  ],
  RETRYABLE_KNOWN_NO_EFFECT: ['ATTEMPT_ACTIVE', 'OPERATOR_REQUIRED', 'SUPERSEDED'],
  TERMINAL_KNOWN_NO_EFFECT: ['SUPERSEDED'],
  OUTCOME_UNKNOWN: [
    'RETRYABLE_KNOWN_NO_EFFECT',
    'TERMINAL_KNOWN_NO_EFFECT',
    'DESIRED_REMOTE_STATE_CONFIRMED',
    'OPERATOR_REQUIRED',
  ],
  DESIRED_REMOTE_STATE_CONFIRMED: [],
  OPERATOR_REQUIRED: [
    'RETRYABLE_KNOWN_NO_EFFECT',
    'TERMINAL_KNOWN_NO_EFFECT',
    'DESIRED_REMOTE_STATE_CONFIRMED',
  ],
  SUPERSEDED: [],
} as const satisfies Readonly<
  Record<RemoteMutationActionState, readonly RemoteMutationActionState[]>
>;

export const REMOTE_MUTATION_INTERACTION_KINDS = ['EXECUTE', 'OBSERVE', 'ADJUDICATE'] as const;
export type RemoteMutationInteractionKind = (typeof REMOTE_MUTATION_INTERACTION_KINDS)[number];

export const REMOTE_MUTATION_INTERACTION_OPERATIONS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'HIDE',
  'READ',
  'DECIDE',
] as const;
export type RemoteMutationInteractionOperation =
  (typeof REMOTE_MUTATION_INTERACTION_OPERATIONS)[number];

export type RemoteMutationInteractionDescriptor =
  | {
      readonly interactionKind: 'EXECUTE';
      readonly interactionOperation: RemoteMutationOperation;
    }
  | {
      readonly interactionKind: 'OBSERVE';
      readonly interactionOperation: 'READ';
    }
  | {
      readonly interactionKind: 'ADJUDICATE';
      readonly interactionOperation: 'DECIDE';
    };

/**
 * Physical interaction classification. A generic 4xx/5xx is never sufficient
 * for a known-no-effect classification: only a provider-specific authoritative
 * response mapping may use a PROVIDER_CONFIRMED_NO_EFFECT_* diagnostic. A 5xx,
 * timeout, disconnect, or failed post-check is OUTCOME_UNKNOWN.
 *
 * OUTCOME_UNKNOWN and OPERATOR_REQUIRED remain immutable historical evidence.
 * Persistence releases either EXECUTE blocker only after a one-time
 * resolved_by_attempt_id points to an eligible terminal same-action OBSERVE or
 * ADJUDICATE attempt; it does not rewrite the original state or first
 * diagnostic.
 */
export const REMOTE_MUTATION_ATTEMPT_STATES = [
  'PREPARED',
  'DISPATCHED',
  'RETRYABLE_KNOWN_NO_EFFECT',
  'TERMINAL_KNOWN_NO_EFFECT',
  'OUTCOME_UNKNOWN',
  'ACTION_EFFECT_ABSENT_CONFIRMED',
  'DESIRED_REMOTE_STATE_CONFIRMED',
  'ACTION_CANCELLED_CONFIRMED',
  'OPERATOR_REQUIRED',
] as const;
export type RemoteMutationAttemptState = (typeof REMOTE_MUTATION_ATTEMPT_STATES)[number];

/**
 * Remote-id evidence is state-correlated. Unknown/operator rows may retain a
 * positive provider hint without treating it as proof; known-no-effect,
 * absent, cancelled, and in-flight rows must remain NULL. Desired-state rows
 * are additionally bound to the concrete operation by the SQL result guard.
 */
export const REMOTE_MUTATION_OBSERVED_REMOTE_ID_POLICY = {
  mustBeNull: [
    'PREPARED',
    'DISPATCHED',
    'RETRYABLE_KNOWN_NO_EFFECT',
    'TERMINAL_KNOWN_NO_EFFECT',
    'ACTION_EFFECT_ABSENT_CONFIRMED',
    'ACTION_CANCELLED_CONFIRMED',
  ],
  optionalPositiveHint: ['OUTCOME_UNKNOWN', 'OPERATOR_REQUIRED'],
  operationBoundProof: ['DESIRED_REMOTE_STATE_CONFIRMED'],
} as const satisfies {
  readonly mustBeNull: readonly RemoteMutationAttemptState[];
  readonly optionalPositiveHint: readonly RemoteMutationAttemptState[];
  readonly operationBoundProof: readonly RemoteMutationAttemptState[];
};

export const REMOTE_MUTATION_ATTEMPT_TRANSITIONS = {
  PREPARED: [
    'DISPATCHED',
    'RETRYABLE_KNOWN_NO_EFFECT',
    'TERMINAL_KNOWN_NO_EFFECT',
    'OPERATOR_REQUIRED',
  ],
  DISPATCHED: [
    'RETRYABLE_KNOWN_NO_EFFECT',
    'TERMINAL_KNOWN_NO_EFFECT',
    'OUTCOME_UNKNOWN',
    'ACTION_EFFECT_ABSENT_CONFIRMED',
    'DESIRED_REMOTE_STATE_CONFIRMED',
    'ACTION_CANCELLED_CONFIRMED',
    'OPERATOR_REQUIRED',
  ],
  RETRYABLE_KNOWN_NO_EFFECT: [],
  TERMINAL_KNOWN_NO_EFFECT: [],
  OUTCOME_UNKNOWN: [],
  ACTION_EFFECT_ABSENT_CONFIRMED: [],
  DESIRED_REMOTE_STATE_CONFIRMED: [],
  ACTION_CANCELLED_CONFIRMED: [],
  OPERATOR_REQUIRED: [],
} as const satisfies Readonly<
  Record<RemoteMutationAttemptState, readonly RemoteMutationAttemptState[]>
>;

export const REMOTE_MUTATION_ATTEMPT_DIAGNOSTIC_CLASSES = [
  'PRECHECK_IDENTITY_MISMATCH',
  'PRECHECK_AUTHORITY_DISABLED',
  'PERMIT_EXPIRED_BEFORE_DISPATCH',
  'PROVIDER_CONFIRMED_NO_EFFECT_UNAUTHORIZED',
  'PROVIDER_CONFIRMED_NO_EFFECT_RATE_LIMIT',
  'PROVIDER_CONFIRMED_NO_EFFECT_CLIENT_REJECTION',
  'REMOTE_SERVER_RESPONSE_UNCERTAIN',
  'REMOTE_TIMEOUT_OR_DISCONNECT',
  'DISPATCH_RECOVERY_UNCERTAIN',
  'POSTCHECK_IDENTITY_MISMATCH',
  'OBSERVATION_CONFLICT',
  'OWNERSHIP_UNCONFIRMED',
  'OPERATOR_ADJUDICATION_REQUIRED',
] as const;
export type RemoteMutationAttemptDiagnosticClass =
  (typeof REMOTE_MUTATION_ATTEMPT_DIAGNOSTIC_CLASSES)[number];

/** Exact state/diagnostic correlation; empty means diagnostic must be NULL. */
export const REMOTE_MUTATION_ATTEMPT_DIAGNOSTICS_BY_STATE = {
  PREPARED: [],
  DISPATCHED: [],
  RETRYABLE_KNOWN_NO_EFFECT: [
    'PRECHECK_AUTHORITY_DISABLED',
    'PERMIT_EXPIRED_BEFORE_DISPATCH',
    'PROVIDER_CONFIRMED_NO_EFFECT_RATE_LIMIT',
  ],
  TERMINAL_KNOWN_NO_EFFECT: [
    'PRECHECK_IDENTITY_MISMATCH',
    'PROVIDER_CONFIRMED_NO_EFFECT_UNAUTHORIZED',
    'PROVIDER_CONFIRMED_NO_EFFECT_CLIENT_REJECTION',
  ],
  OUTCOME_UNKNOWN: [
    'REMOTE_SERVER_RESPONSE_UNCERTAIN',
    'REMOTE_TIMEOUT_OR_DISCONNECT',
    'DISPATCH_RECOVERY_UNCERTAIN',
    'POSTCHECK_IDENTITY_MISMATCH',
  ],
  ACTION_EFFECT_ABSENT_CONFIRMED: [],
  DESIRED_REMOTE_STATE_CONFIRMED: [],
  ACTION_CANCELLED_CONFIRMED: [],
  OPERATOR_REQUIRED: [
    'OBSERVATION_CONFLICT',
    'OWNERSHIP_UNCONFIRMED',
    'OPERATOR_ADJUDICATION_REQUIRED',
  ],
} as const satisfies Readonly<
  Record<RemoteMutationAttemptState, readonly RemoteMutationAttemptDiagnosticClass[]>
>;

/** Only these terminal resolution results may release an UNKNOWN/OPERATOR EXECUTE. */
export const REMOTE_MUTATION_RESOLUTION_ELIGIBLE_ATTEMPT_STATES = [
  'ACTION_EFFECT_ABSENT_CONFIRMED',
  'DESIRED_REMOTE_STATE_CONFIRMED',
  'ACTION_CANCELLED_CONFIRMED',
] as const satisfies readonly RemoteMutationAttemptState[];

export const REMOTE_MUTATION_ATTEMPT_STATES_BY_INTERACTION_KIND = {
  EXECUTE: [
    'PREPARED',
    'DISPATCHED',
    'RETRYABLE_KNOWN_NO_EFFECT',
    'TERMINAL_KNOWN_NO_EFFECT',
    'OUTCOME_UNKNOWN',
    'DESIRED_REMOTE_STATE_CONFIRMED',
    'OPERATOR_REQUIRED',
  ],
  OBSERVE: [
    'PREPARED',
    'DISPATCHED',
    'RETRYABLE_KNOWN_NO_EFFECT',
    'TERMINAL_KNOWN_NO_EFFECT',
    'OUTCOME_UNKNOWN',
    'ACTION_EFFECT_ABSENT_CONFIRMED',
    'DESIRED_REMOTE_STATE_CONFIRMED',
    'OPERATOR_REQUIRED',
  ],
  ADJUDICATE: [
    'PREPARED',
    'DISPATCHED',
    'RETRYABLE_KNOWN_NO_EFFECT',
    'TERMINAL_KNOWN_NO_EFFECT',
    'OUTCOME_UNKNOWN',
    'ACTION_EFFECT_ABSENT_CONFIRMED',
    'DESIRED_REMOTE_STATE_CONFIRMED',
    'ACTION_CANCELLED_CONFIRMED',
    'OPERATOR_REQUIRED',
  ],
} as const satisfies Readonly<
  Record<RemoteMutationInteractionKind, readonly RemoteMutationAttemptState[]>
>;

export type RemoteMutationInteractionResult =
  | {
      readonly interactionKind: 'EXECUTE';
      readonly interactionOperation: RemoteMutationOperation;
      readonly state: (typeof REMOTE_MUTATION_ATTEMPT_STATES_BY_INTERACTION_KIND.EXECUTE)[number];
    }
  | {
      readonly interactionKind: 'OBSERVE';
      readonly interactionOperation: 'READ';
      readonly state: (typeof REMOTE_MUTATION_ATTEMPT_STATES_BY_INTERACTION_KIND.OBSERVE)[number];
    }
  | {
      readonly interactionKind: 'ADJUDICATE';
      readonly interactionOperation: 'DECIDE';
      readonly state: (typeof REMOTE_MUTATION_ATTEMPT_STATES_BY_INTERACTION_KIND.ADJUDICATE)[number];
    };

export type RemoteMutationIntentTransitionTableIsExhaustive = Assert<
  SameMembers<keyof typeof REMOTE_MUTATION_INTENT_TRANSITIONS, RemoteMutationIntentState>
>;
export type RemoteMutationActionTransitionTableIsExhaustive = Assert<
  SameMembers<keyof typeof REMOTE_MUTATION_ACTION_TRANSITIONS, RemoteMutationActionState>
>;
export type RemoteMutationAttemptTransitionTableIsExhaustive = Assert<
  SameMembers<keyof typeof REMOTE_MUTATION_ATTEMPT_TRANSITIONS, RemoteMutationAttemptState>
>;
export type RemoteMutationAttemptDiagnosticTableIsExhaustive = Assert<
  SameMembers<keyof typeof REMOTE_MUTATION_ATTEMPT_DIAGNOSTICS_BY_STATE, RemoteMutationAttemptState>
>;
export type RemoteMutationObservedRemoteIdPolicyIsExhaustive = Assert<
  SameMembers<
    | (typeof REMOTE_MUTATION_OBSERVED_REMOTE_ID_POLICY.mustBeNull)[number]
    | (typeof REMOTE_MUTATION_OBSERVED_REMOTE_ID_POLICY.optionalPositiveHint)[number]
    | (typeof REMOTE_MUTATION_OBSERVED_REMOTE_ID_POLICY.operationBoundProof)[number],
    RemoteMutationAttemptState
  >
>;
export type RemoteMutationAttemptDiagnosticValuesAreExact = Assert<
  SameMembers<
    (typeof REMOTE_MUTATION_ATTEMPT_DIAGNOSTICS_BY_STATE)[keyof typeof REMOTE_MUTATION_ATTEMPT_DIAGNOSTICS_BY_STATE][number],
    RemoteMutationAttemptDiagnosticClass
  >
>;
export type RemoteMutationAttemptKindsCoverEveryState = Assert<
  SameMembers<
    (typeof REMOTE_MUTATION_ATTEMPT_STATES_BY_INTERACTION_KIND)[keyof typeof REMOTE_MUTATION_ATTEMPT_STATES_BY_INTERACTION_KIND][number],
    RemoteMutationAttemptState
  >
>;
export type RemoteMutationTargetBlockingSetIsExactNonterminalComplement = Assert<
  SameMembers<
    (typeof REMOTE_MUTATION_TARGET_BLOCKING_INTENT_STATES)[number],
    Exclude<RemoteMutationIntentState, RemoteMutationIntentTerminalState>
  >
>;
export type RemoteMutationTerminalIntentStatesHaveNoOutgoingTransitions = Assert<
  (typeof REMOTE_MUTATION_INTENT_TRANSITIONS)['LOCAL_COMMITTED']['length'] extends 0
    ? (typeof REMOTE_MUTATION_INTENT_TRANSITIONS)['COMPENSATED']['length'] extends 0
      ? (typeof REMOTE_MUTATION_INTENT_TRANSITIONS)['CANCELLED']['length'] extends 0
        ? true
        : false
      : false
    : false
>;
